import { User } from '@supabase/supabase-js';

// Configuration
// ⚡ SWITCH BACKEND: Comment/uncomment ONE of these lines
//export const GCP_BACKEND_URL = 'http://localhost:8080';                                          // LOCAL
//export const GCP_BACKEND_URL = 'https://ndvi5-analysis-api-258557095482.us-central1.run.app';    // CLOUD (ndvi5 - old)
export const GCP_BACKEND_URL = 'https://ndvi6-analysis-api-258557095482.us-central1.run.app';      // CLOUD (ndvi6 - current)
// Master switch for paid subscriptions. While false, every path that could
// start a new Stripe subscription is hidden or blocked. The backend enforces
// the same thing independently (SUBSCRIPTIONS_ENABLED env var), so turning
// this back on alone is not enough to re-open checkout.
export const SUBSCRIPTIONS_ENABLED = false;

export const SUPABASE_URL = 'https://quvbsftdyaxjqlwnjtxv.supabase.co';
export const SUPABASE_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InF1dmJzZnRkeWF4anFsd25qdHh2Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjA1MzI0OTEsImV4cCI6MjA3NjEwODQ5MX0.BpxqHKTvmSghuNDtLAKU-hszFJzey_oyn5_hDRZeqTg";

// Shared Types
export interface Analysis {
  /** Same as session_id (analyses are keyed by session_id in Cloud SQL). */
  id: string;
  user_id: string;
  user_email: string;
  session_id: string;
  query: string;
  analysis_type: string;
  location: string;
  is_shared: boolean;
  created_at: string;
  shared_at?: string;
  conversation_id?: string;
  conversation_context?: string | ConversationContext;
  map_url?: string;
  chart_url?: string;
  thumbnail_url?: string;
  animation_url?: string;
  preview_url?: string;
  likes?: number;
  /** true/false when the request carried a user_id; null otherwise */
  liked?: boolean | null;
  /** Public display name (author_name, else e-mail prefix) — set by backend */
  user_name?: string | null;
  author_name?: string | null;
  /** True when the GCS result files were purged; show placeholder, hide from Community */
  artifacts_expired?: boolean;
  // Detail fields saved by backend
  location_lat?: number;
  location_lng?: number;
  start_year?: number;
  end_year?: number;
  parameters?: Record<string, any>;
  processing_time_seconds?: number;
  metadata?: Record<string, any>;
  conversation_history?: Array<{ role: string; content: string }>;
}

export interface ConversationContext {
  history: ConversationHistoryItem[];
  context: any;
}

export interface ConversationHistoryItem {
  query: string;
  response: string | null;
  timestamp: Date;
  isCompletionMessage?: boolean;
}

export interface ConversationGroup {
  conversation_id: string;
  analyses: Analysis[];
  first_query: string;
  location: string;
  created_at: string;
  shared_at?: string;
  count: number;
  user_name?: string;
  timeline?: TimelineItem[];
}

export interface TimelineItem {
  type: 'message' | 'analysis';
  timestamp: Date;
  role?: 'user' | 'assistant';
  content?: string;
  message_type?: string;
  [key: string]: any;
}

export interface LogEntry {
  timestamp: string;
  displayTime: string;
  level: 'info' | 'success' | 'warning' | 'error';
  message: string;
  source: 'frontend' | 'backend';
}

export interface UserCredits {
  user_id: string;
  credits: number;
  total_used: number;
}

export interface Subscription {
  user_id: string;
  plan_type: 'free' | 'pro' | 'enterprise';
  status: 'active' | 'inactive' | 'canceled';
  stripe_customer_id?: string;
}

export interface CreditTransaction {
  id: number;
  user_id: string;
  amount: number;
  balance_after: number;
  description: string;
  created_at: string;
}

export type AuthView = 'login' | 'signup' | 'forgot' | 'reset';
export type PageType = 'home' | 'myanalyses' | 'community' | 'about' | 'pricing' | 'billing' | 'settings';

// Component Props
export interface NavigationProps {
  user: User | null;
  setUser: (user: User | null) => void;
  setShowAuthModal: (show: boolean) => void;
  currentPage: PageType;
  setCurrentPage: (page: PageType) => void;
}

export interface LandingPageProps {
  user: User | null;
  setShowAuthModal: (show: boolean) => void;
  setAuthView: (view: AuthView) => void;
}

export interface MyAnalysesPageProps {
  user: User | null;
  setShowAuthModal: (show: boolean) => void;
  setAuthView: (view: AuthView) => void;
  setIsFullPageView: (isFull: boolean) => void;
}

export interface CommunityPageProps {
  user: User | null;
  setIsFullPageView: (isFull: boolean) => void;
}

export interface PricingPageProps {
  setCurrentPage: (page: PageType) => void;
}

export interface BillingPageProps {
  user: User | null;
  setShowAuthModal: (show: boolean) => void;
  setAuthView: (view: AuthView) => void;
}

export interface AuthModalProps {
  authView: AuthView;
  setAuthView: (view: AuthView) => void;
  setShowAuthModal: (show: boolean) => void;
}

export interface AuthFormProps {
  setAuthView: (view: AuthView) => void;
}
